import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../lib/api';
import toast from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';

export default function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');

  const verifyMutation = useMutation({
    mutationFn: (token: string) => authApi.verifyEmail(token),
    onSuccess: () => {
      setStatus('success');
      toast.success('Email verified successfully!');
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    },
    onError: (error: any) => {
      setStatus('error');
      toast.error(error.response?.data?.error || 'Failed to verify email');
    },
  });

  useEffect(() => {
    if (token) {
      verifyMutation.mutate(token);
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <Logo className="h-10" />
        </Link>

        <div className="mt-8 bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10">
          <div className="text-center">
            {status === 'verifying' && (
              <>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-ocean-100 animate-pulse">
                  <EnvelopeIcon className="h-6 w-6 text-ocean-600" />
                </div>
                <h2 className="mt-4 text-2xl font-bold text-navy-900">
                  Verifying your email...
                </h2>
                <p className="mt-2 text-gray-600">
                  Please wait while we confirm your email address.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-mint-100">
                  <CheckCircleIcon className="h-6 w-6 text-mint-600" />
                </div>
                <h2 className="mt-4 text-2xl font-bold text-navy-900">
                  Email verified!
                </h2>
                <p className="mt-2 text-gray-600">
                  Your email has been successfully verified. You can now access all features of Clarid.
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  Redirecting to dashboard...
                </p>
                <div className="mt-6">
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-ocean-600 hover:bg-ocean-700"
                  >
                    Go to dashboard
                  </Link>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <XCircleIcon className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="mt-4 text-2xl font-bold text-navy-900">
                  Verification failed
                </h2>
                <p className="mt-2 text-gray-600">
                  The verification link is invalid or has expired. Please request a new verification email.
                </p>
                <div className="mt-6 space-y-3">
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-ocean-600 hover:bg-ocean-700"
                  >
                    Go to dashboard
                  </Link>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Back to login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
